import { useReveal, useParallax } from "../hooks/useReveal";
import { useLanguage } from "../context/LanguageContext";

// The Unstoppable Mindset — philosophy/emotional editorial section
// Real Cabo Unstoppable brand photography
const MANIFESTO_BG = "/images/mindset-manifesto.webp";
const RESILIENCE_IMG = "/images/mindset-resilience.webp";
const FOCUS_IMG = "/images/mindset-focus.webp";
const AMBITION_IMG = "/images/mindset-ambition.webp";
const DAILY_EFFORT_IMG = "/images/mindset-daily.webp";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, revealed] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Parallax = ({ speed = 0.18, children, className = "" }) => {
  const [ref, offset] = useParallax(speed);
  return (
    <div
      ref={ref}
      className={className}
      style={{ transform: `translate3d(0, ${offset}px, 0)`, willChange: "transform" }}
    >
      {children}
    </div>
  );
};

export const MindsetSection = () => {
  const { t } = useLanguage();
  const m = t.mindsetSection;
  const lines = m.manifesto;

  return (
    <section
      id="mindset"
      data-testid="mindset-section"
      className="relative overflow-hidden text-white"
      style={{
        background:
          "linear-gradient(180deg, #000000 0%, #050505 40%, #0a0a0a 60%, #000000 100%)",
      }}
    >
      {/* Subtle noise + grid backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-40 grid-lines" />
      <div className="noise absolute inset-0" />

      {/* Chapter opener */}
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 pt-28 md:pt-40 relative">
        <Reveal>
          <p className="overline mb-6">{m.chapterOverline}</p>
        </Reveal>
        <Reveal delay={100}>
          <h2
            data-testid="mindset-title"
            className="h-display text-white text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] leading-[0.85]"
          >
            {m.titleLine1}
            <br />
            <span className="text-yellow-400">{m.titleLine2}</span>
          </h2>
        </Reveal>
        <Reveal delay={220}>
          <p
            data-testid="mindset-subtitle"
            className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed"
          >
            {m.subtitle}
          </p>
        </Reveal>
      </div>

      {/* Emotional manifesto — big text over image */}
      <div className="relative mt-24 md:mt-32">
        <div className="absolute inset-0 overflow-hidden">
          <Parallax speed={0.2} className="absolute inset-0">
            <img
              src={MANIFESTO_BG}
              alt=""
              loading="lazy"
              className="w-full h-[120%] object-cover object-center image-cinematic opacity-45"
            />
          </Parallax>
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 py-32 md:py-48">
          <ul className="space-y-6 md:space-y-10">
            {lines.map((line, i) => (
              <Reveal
                key={i}
                delay={i * 120}
                className=""
              >
                <li
                  data-testid={`mindset-line-${i}`}
                  className="flex items-baseline gap-4 md:gap-6"
                >
                  <span className="font-display text-yellow-400 text-lg md:text-xl w-6 md:w-10 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-editorial text-2xl md:text-4xl leading-tight text-white/95 tracking-tight">
                    {line}
                  </span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>

      {/* Image bento — quiet moments of focus */}
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[300px] md:auto-rows-[340px] gap-3">
          <Reveal className="md:col-span-8 md:row-span-2">
            <div className="relative w-full h-full overflow-hidden group bg-zinc-950">
              <img
                src={RESILIENCE_IMG}
                alt="Confidence on the coast of Los Cabos"
                loading="lazy"
                className="w-full h-full object-cover object-[center_20%] image-cinematic transition-transform duration-[1400ms] group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="overline mb-2">{m.resilienceOverline}</p>
                <p className="font-display text-3xl md:text-4xl text-white leading-tight">
                  {m.resilienceTitle}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={140} className="md:col-span-4 md:row-span-1">
            <div className="relative w-full h-full overflow-hidden group bg-zinc-950">
              <img
                src={FOCUS_IMG}
                alt="A moment of focus"
                loading="lazy"
                className="w-full h-full object-cover object-[center_25%] image-cinematic transition-transform duration-[1400ms] group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <p className="overline">{m.focusOverline}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={260} className="md:col-span-4 md:row-span-1">
            <div className="relative w-full h-full overflow-hidden group bg-zinc-950">
              <img
                src={AMBITION_IMG}
                alt="Ambition, together"
                loading="lazy"
                className="w-full h-full object-cover object-[center_25%] image-cinematic transition-transform duration-[1400ms] group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <p className="overline">{m.ambitionOverline}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={200} className="md:col-span-6 md:row-span-1">
            <div className="relative w-full h-full overflow-hidden group bg-zinc-950">
              <img
                src={DAILY_EFFORT_IMG}
                alt="Show up together, every day"
                loading="lazy"
                className="w-full h-full object-cover object-[center_35%] image-cinematic transition-transform duration-[1400ms] group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <p className="overline mb-1">{m.dailyEffortOverline}</p>
                <p className="font-display text-2xl text-white leading-none">
                  {m.dailyEffortTitle}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={320} className="md:col-span-6 md:row-span-1 flex">
            <div className="relative w-full h-full overflow-hidden bg-yellow-400 text-black p-8 md:p-10 flex flex-col justify-between">
              <p className="overline text-black/70">{m.forDecidersOverline}</p>
              <p className="font-display uppercase text-3xl md:text-4xl leading-[0.95] tracking-tight">
                {m.forDecidersBody}
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Closing statement */}
      <div className="max-w-4xl mx-auto px-6 md:px-10 pb-32 md:pb-40 text-center">
        <Reveal>
          <p className="font-display text-white text-4xl sm:text-5xl md:text-7xl uppercase leading-[0.95] tracking-tight">
            {m.closingLine1}
            <br />
            {m.closingLine2Before}
            <span className="text-yellow-400">{m.closingLine2Highlight}</span>
          </p>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-10 text-lg md:text-xl text-white/70 max-w-xl mx-auto leading-relaxed">
            {m.closingSub}
          </p>
        </Reveal>
        <Reveal delay={340}>
          <div className="mt-12 inline-flex items-center gap-5">
            <span className="w-10 h-px bg-yellow-400" aria-hidden />
            <span className="text-[11px] uppercase tracking-[0.4em] font-bold text-white/80">
              BE UNSTOPPABLE
            </span>
            <span className="w-10 h-px bg-yellow-400" aria-hidden />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
